import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { Box, Paper, useMediaQuery } from '@mui/material'

/**
 * StickyPeekHero — hero stays static in flow; a compact overlay slides in
 * from the top once the hero has scrolled past the viewport.
 *
 * @description
 *   Two independent bars instead of one shape-shifting sticky element.
 *   The expanded hero lives in document flow (scrolls away normally).
 *   The compact overlay stays mounted (so both fade directions animate) but
 *   is hidden via opacity + translateY + `visibility`; it fades + slides down
 *   once the hero's bottom edge has left the viewport by `heroExitBufferPx`
 *   pixels.
 *
 *   🔴 `visibility` is load-bearing, not decoration. Until 2026-08-09 the
 *   overlay was hidden with `opacity: 0` + `pointerEvents: 'none'` alone,
 *   which hides it VISUALLY ONLY: the subtree stayed in the accessibility
 *   tree and in the tab order, so every interactive node in `compact` was a
 *   second, invisible, keyboard-reachable duplicate of its twin in
 *   `expanded`. Measured in Chromium on 2026-08-09: `getByRole('button',
 *   { name })` resolved to 2, the hidden copy had `tabIndex=0`, and pressing
 *   Enter on it fired a real click — `pointer-events` blocks the mouse, not
 *   the keyboard. `visibility: hidden` is what removes a subtree from both,
 *   and it keeps the layout box, so nothing that measures geometry shifts.
 *
 *   Removes the pre-refactor bugs: the sticky→fixed handoff flash, the
 *   scroll feedback loop from height placeholders, the boundary jitter,
 *   and the mount flicker when the browser restores scroll past the hero.
 *
 * @useWhen
 *   - Page hero that needs a compact version pinned on scroll
 *   - Case detail / PO detail / any detail page with a scrollable body
 *   - Any place you'd reach for `position: sticky` + `collapsed ? compact : expanded`
 *
 * @dontUseFor
 *   - A pure always-sticky header (just use position: sticky directly)
 *   - Full-page modal overlays — use Dialog or SlidePanel
 *   - Pages without a clearly-separated hero region
 *   - A `compact` node that sets `visibility: visible` on itself — that
 *     re-declares the inherited property and puts the hidden copy back into
 *     the accessibility tree and the tab order, silently
 *
 *   🔴 `data-sticky-peek="kompakt"` — VERTRAG, nicht Kosmetik (R16 W10,
 *   01.11.2026). Die Kompaktleiste ist `position: fixed` und deckte das
 *   Sprungziel der PO-Checkliste zu. `PODetail.scrollToSection` misst ihre
 *   Unterkante an genau diesem Attribut, statt eine Hoehe zu raten (Muster
 *   K-13 aus `VendorAnschriften.jsx`). Wer die Leiste umbaut und das Attribut
 *   loescht, macht den Sprung wieder blind — und **keine** Probe wird rot:
 *   der Sprung landet dann unter der Leiste, sichtbar falsch, technisch gruen.
 *   Es steht nur im `compact`-Zweig, weil nur der ueber dem Inhalt liegt.
 *
 * @related
 *   - MUI position:sticky — the naive version this replaces
 *   - POStatusBar — die verbliebene Aufrufstelle. `CaseStatusBar` war die zweite
 *     und ist am 2026-09-08 geloescht (Block 2 Stufe 3a); der RMA-Fallkopf hat
 *     keinen Sticky-Hero mehr.
 *
 * @reuseHints
 *   - Product detail page could adopt this for the sticky buy-box
 *   - Vendor detail page for the summary/actions strip
 *   - Reports page hero with primary CTA
 *
 * @props
 *   expanded — node rendered in document flow; the "full" hero
 *   compact  — node rendered in the fixed overlay when scrolled past. FALSY
 *     (e.g. a caller's `!isMobile && <Bar/>`) renders no overlay at all —
 *     without that guard the glass Paper still faded in, empty, on scroll
 *     (measured on PO mobile 2026-08-09: a 366×16 bar with zero children).
 *     Note this is an idiom guard, not a semantic one: `<></>`, `[]` and `' '`
 *     are truthy and still produce an empty panel.
 *   heroExitBufferPx — pixels past hero bottom before compact appears (default 50)
 *   topOffsetPx — responsive top offset for the fixed bar; default { xs: 64, sm: 72 }
 *   zIndex — default 200
 */
export default function StickyPeekHero({
  expanded,
  compact,
  heroExitBufferPx = 50,
  // Clears the app bar. `sm: 72` is the desktop app bar (measured 64) plus an
  // 8px SP.tight gap. `xs: 64` is NOT that arithmetic — the mobile app bar
  // (`SidebarAndHeaderBar.jsx`, Toolbar minHeight 44 + 1px border) measures 45,
  // so this leaves ~19px. Kept as-is rather than "corrected" to 53: it is the
  // shipped spacing, and tightening it is a design decision, not a bug fix.
  topOffsetPx = { xs: 64, sm: 72 },
  zIndex = 200,
}) {
  const wrapperRef = useRef(null)
  const [collapsed, setCollapsed] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [fixedRect, setFixedRect] = useState({ left: 0, width: 0 })

  // 🔴 The app-wide reduced-motion reset (`theme.js`, `variables.css`) forces
  // `transition-duration` to 0.01ms but does NOT touch `transition-delay`. So
  // the 220ms hide-delay below would SURVIVE it: opacity would drop to 0
  // instantly while `visibility` stayed `visible` for another 220ms — an
  // invisible, focusable, AT-exposed duplicate. That is precisely the defect
  // this component was fixed for, reopened in a small window. Measured under
  // `prefers-reduced-motion: reduce` before this guard existed: at t≈100ms
  // after scroll-up, `visibility: visible`, `opacity: 0`, focus still on the
  // compact CTA. So: no motion, no delay.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  // useLayoutEffect so state is correct BEFORE first paint.
  // Without this, a browser-restored scroll past the hero makes the bar
  // animate in on load (reads as a flicker).
  useLayoutEffect(() => {
    if (!wrapperRef.current) return
    const onScroll = () => {
      const rect = wrapperRef.current?.getBoundingClientRect()
      if (!rect) return
      setCollapsed(rect.bottom < -heroExitBufferPx)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [heroExitBufferPx])

  useLayoutEffect(() => {
    if (!wrapperRef.current) return
    const measure = () => {
      const r = wrapperRef.current?.getBoundingClientRect()
      if (r) setFixedRect({ left: r.left, width: r.width })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // Flip mounted after first paint so subsequent state changes animate;
  // the initial state snap does not.
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(t)
  }, [])

  return (
    <>
      <Box ref={wrapperRef}>{expanded}</Box>

      {/* No compact node → no overlay. A caller that opts out per breakpoint
          (`!isMobile && <Bar/>`) otherwise still got the glass Paper, empty,
          fading in on every scroll past the hero. */}
      {compact ? (
      <Box
        // 🔴 U4 (01.11.2026): diese Leiste ist `position: fixed` und deckte das
        //    Sprungziel der PO-Checkliste zu. Sie traegt jetzt ein Merkmal, an
        //    dem `PODetail.scrollToSection` ihre Unterkante MISST, statt eine
        //    Hoehe zu raten (Muster K-13, `VendorAnschriften.jsx`).
        data-sticky-peek="kompakt"
        sx={{
        position: 'fixed',
        // The overlay is out of flow, but a caller whose container applies
        // adjacent-sibling spacing (PO's `MuiStack`) still hands it a margin —
        // measured 24px on PO, which pushed its compact bar to 32px below the
        // app bar while RMA's sat at the intended 8px. Same primitive, two
        // gaps. Pinning it here makes the overlay immune to the caller.
        margin: 0,
        top: topOffsetPx,
        left: fixedRect.left,
        width: fixedRect.width,
        zIndex,
        opacity: collapsed ? 1 : 0,
        transform: collapsed ? 'translateY(0)' : 'translateY(-16px)',
        pointerEvents: collapsed ? 'auto' : 'none',
        // Keeps the hidden copy out of the a11y tree AND the tab order. See
        // the 🔴 block in the header — opacity alone does neither.
        visibility: collapsed ? 'visible' : 'hidden',
        // `visibility` is a stepped property, so it needs its own
        // direction-aware delay or it destroys one of the two fades:
        //   showing → flip at 0s, else the opacity animation runs while the
        //             panel is still hidden and it snaps in at the end;
        //   hiding  → wait the full 220ms, else the panel vanishes instantly
        //             instead of fading out.
        // Verified in Chromium 2026-08-09, independently twice: one frame into
        // the fade-in the overlay is already `visible` while opacity is still
        // near 0; ~100ms into the fade-out it is still `visible` at opacity
        // ~.25-.29; `hidden` only once the fade ends. (The exact opacity
        // samples are rAF-timing dependent — the ORDERING is the contract, not
        // the numbers.)
        //
        // `reduceMotion` collapses the hide-delay to 0s — see the 🔴 note by
        // the hook. Without it the delay outlives the reduced-motion reset,
        // which zeroes duration only.
        transition: mounted
          ? `opacity 220ms ease, transform 220ms ease, visibility 0s linear ${
              collapsed || reduceMotion ? '0s' : '220ms'
            }`
          : 'none',
      }}>
        <Paper elevation={0} sx={{
          py: 0.875,
          px: { xs: 1.5, sm: 2.5 },
          background: (t) => t.palette.mode === 'light' ? 'rgba(248,250,252,0.94)' : 'rgba(15,23,42,0.94)',
          backdropFilter: 'blur(14px) saturate(160%)',
          WebkitBackdropFilter: 'blur(14px) saturate(160%)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          boxShadow: (t) => t.palette.mode === 'light'
            ? '0 4px 20px rgba(15,23,42,0.10)'
            : '0 4px 20px rgba(0,0,0,0.40)',
        }}>
          {compact}
        </Paper>
      </Box>
      ) : null}
    </>
  )
}
